import { useCallback } from "react";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";
import { ChevronDownIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import { components as selectComponents } from "react-select";
import { Building, SquareKanban, Users } from "lucide-react";
import { ContentTypes } from "../../constants/action-types";
import { SelectField } from "../../components/ui";
import { search } from "../../api/ProjectApi";

const SelectItem = ({ selectedItem, onSelectItem, isDisabled = false }) => {
  const { t } = useTranslation();
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const projectId = project.get("id");

  const itemType = selectedItem?.type || ContentTypes.SALE;
  const itemValue = selectedItem?.value ? selectedItem : null;

  const ContentTypeIcon = {
    [ContentTypes.SALE]: <SquareKanban className="w-3.5 h-3.5 text-gray-600" />,
    [ContentTypes.CONTACT]: <Users className="w-3.5 h-3.5 text-gray-600" />,
    [ContentTypes.COMPANY]: <Building className="w-3.5 h-3.5 text-gray-600" />,
  };

  const optionItems = [
    { value: ContentTypes.SALE, label: t("Deal") },
    { value: ContentTypes.CONTACT, label: t("Contact") },
    { value: ContentTypes.COMPANY, label: t("Account") },
  ];

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      const searchType = itemType || "SALE";
      search({
        projectId,
        pageSize: 5,
        types: [searchType],
        filters: {
          name: {
            operator: "regex",
            values: [inputValue],
          },
        },
      }).then((result) => {
        const datas = fromJS(result)
          .getIn(["pageResult", "content"])
          .map((item) => ({
            value: item.get("id"),
            label: item.getIn(["data", "name", "value"]),
            data: item,
          }));
        callback(datas.toJS());
      });
    },
    [projectId, itemType]
  );

  return (
    <div className="w-full flex items-center">
      <div>
        <SelectField
          isDisabled={isDisabled}
          className="w-max"
          value={optionItems.find((item) => item.value === itemType)}
          onChange={(value) => {
            onSelectItem(
              fromJS({
                type: value?.value,
              })
            );
          }}
          options={optionItems}
          formatOptionLabel={(option, { context }) => {
            if (context === "value") {
              return (
                <div
                  key={option.value}
                  className="w-full hover:text-blue-600 flex items-center gap-1.5"
                >
                  {ContentTypeIcon[option.value]}
                  <span className="leading-none">{t(option.label)}</span>
                </div>
              );
            }
            return (
              <div
                key={option.value}
                className="w-full hover:text-blue-600 flex items-center gap-1.5"
              >
                {ContentTypeIcon[option.value]}
                <span className="leading-none">{t(option.label)}</span>
              </div>
            );
          }}
          dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
          stylesOverride={{
            control: (provided, state) => ({
              ...provided,
              minHeight: 38,
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              backgroundColor: "#fbf9fa",
              borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
              boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
              ":hover": {
                borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
              },
              fontSize: 14,
            }),
            menu: (base) => ({
              ...base,
              width: "max-content",
              minWidth: "100%",
            }),
          }}
        />
      </div>
      <div className="w-full">
        <AsyncSelect
          key={itemType}
          isDisabled={isDisabled}
          placeholder={t("Search...")}
          value={itemValue}
          onChange={(value) => {
            if (value?.data) {
              onSelectItem(fromJS(value?.data));
            } else {
              onSelectItem(
                fromJS({
                  type: itemType,
                })
              );
            }
          }}
          isClearable
          defaultOptions
          loadOptions={loadOptions}
          menuPortalTarget={document.body}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: (indicatorProps) => (
              <selectComponents.DropdownIndicator {...indicatorProps}>
                <ChevronUpDownIcon className="w-3.5 h-3.5 text-gray-500" />
              </selectComponents.DropdownIndicator>
            ),
          }}
          styles={{
            control: (provided, state) => ({
              ...provided,
              minHeight: 38,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,
              borderLeft: "none",
              backgroundColor: state.isDisabled ? "#fbf9fa" : "white",
              borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
              boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
              ":hover": {
                borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
              },
              fontSize: 14,
            }),
            placeholder: (provided) => ({
              ...provided,
              color: "#6b7280",
              fontWeight: 500,
            }),
            singleValue: (provided, state) => ({
              ...provided,
              color: state.isDisabled ? "#6b7280" : "#111827",
              fontWeight: 500,
            }),
            option: (provided, state) => ({
              ...provided,
              fontSize: 14,
              fontWeight: 500,
              backgroundColor: state.isSelected ? "#dbeafe" : state.isFocused ? "#f3f4f6" : "white",
              color: state.isDisabled ? "#6b7280" : "#111827",
              ":active": {
                backgroundColor: "#e5e7eb",
              },
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 9999,
            }),
          }}
        />
      </div>
    </div>
  );
};

export default SelectItem;
